import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'formik';
import { TextField } from 'formik-mui';
import { DragDropContext } from 'react-beautiful-dnd';

import { makeStyles } from '@mui/styles';
import {
  Grid,
  Button,
  Typography,
  IconButton,
  CircularProgress,
  Box,
  TextField as MuiTextField,
  Autocomplete,
} from '@mui/material';

import { move } from 'ramda';
import { nanoid } from 'nanoid';
import { upperFirst } from 'lodash';
import { AddCircleOutline, Delete } from '@mui/icons-material';
import CustomDialog from '../CustomDialog/index';
import Droppable from '../DragAndDrop/Droppable';
import Draggable from '../DragAndDrop/Draggable';
import { getIngredientsState } from '../../store/ingredients/selectors';
import { fetchIngredients } from '../../store/ingredients';
import AddIngredient from '../../pages/Ingredients/AddIngredient';
import { getMenuItemsState } from '../../store/menuItems/selectors';
import { fetchMenuItems } from '../../store/menuItems';
import shouldLoad from '../../shared/utils/shouldLoad';

const useStyles = makeStyles((theme) => ({
  title: { marginTop: theme.spacing(3), marginBottom: theme.spacing(2) },
  optionField: {
    paddingLeft: theme.spacing(6),
  },
  dragIcon: {
    left: 0,
    top: '23.5%',
  },
  ingredientAutocomplete: {
    marginTop: 16,
  },
}));

const upperAll = (str) =>
  str
    .split(' ')
    .map((word) => upperFirst(word))
    .join(' ');

const sortOptions = (options, compareKey = 'ingredientName') =>
  options.sort((a, b) => a[compareKey]?.localeCompare(b[compareKey]));

const getLabel = (optionId) => {
  if (optionId === '') return 'Ingredient or Item';
  if (optionId.split('-')[0] === 'pai') return 'Item';
  return 'Ingredient';
};

const ModifierOptions = ({ itemOptions, allowReordering, onReorder, setFieldValue, readonly }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { loading, data: options } = useSelector(getIngredientsState);
  const { data: menuItems } = useSelector(getMenuItemsState);
  const [isSizeDialogOpen, setIsSizeDialogOpen] = useState(false);
  const [updatingOption, setUpdatingOption] = useState(null);
  const [showDuplicateHelperText, setShowDuplicateHelperText] = useState(false);
  const [newSize, setNewSize] = useState(null);
  const onDragEnd = async ({ destination, source }) => {
    if (!destination) {
      return;
    }
    if (onReorder)
      onReorder({
        itemOptions: move(source.index, destination.index, itemOptions),
      });
    setFieldValue('modifierItems', move(source.index, destination.index, itemOptions));
  };

  // after adding a new ingredient from the drop down, clear temporary state that allowed
  // immediate selection in the dropdown.
  useEffect(() => {
    if (updatingOption && newSize) {
      const updatingOptionObj =
        updatingOption &&
        newSize &&
        options.find((option) => option.ingredientName === upperAll(newSize.ingredientName));
      const index = updatingOption.split('.')[1];
      setFieldValue(`modifierItems.${index}.itemName`, updatingOptionObj.ingredientName);
      setFieldValue(`modifierItems.${index}.itemId`, updatingOptionObj.ingredientId);
      setUpdatingOption(null);
      setNewSize(null);
    }
  }, [updatingOption, newSize, options, setFieldValue]);

  useEffect(() => {
    if (shouldLoad(getIngredientsState)) dispatch(fetchIngredients());
    if (shouldLoad(getMenuItemsState)) dispatch(fetchMenuItems());
  }, [dispatch]);

  useEffect(() => {
    if (options) {
      const optionIsThere = options.findIndex((e) => e.itemName === newSize?.itemName);
      if (!isSizeDialogOpen && newSize?.itemName && optionIsThere !== -1) {
        if (updatingOption) {
          setFieldValue(updatingOption, newSize?.itemName);
        }
      }
    }
  }, [newSize, options, isSizeDialogOpen, setFieldValue, updatingOption]);

  const handleCloseSizeDialog = async (localNewSize) => {
    await dispatch(fetchIngredients());
    setNewSize(localNewSize);
    setIsSizeDialogOpen(false);
  };

  const itemsWithIngredientId = useMemo(
    () =>
      menuItems &&
      menuItems.map((item) => ({ ...item, itemId: `pai-${item.itemId}`, isItem: true })),
    [menuItems],
  );

  const searchableOptions = useMemo(() => {
    const sortedOptions = options && sortOptions([...options]);
    const sortedItems =
      itemsWithIngredientId && sortOptions([...itemsWithIngredientId], 'itemName');

    // Filter brand items so they don't appear selectable for venues
    const filterOutBrandItems = sortedItems && sortedItems.filter((item) => !item.brandId);

    return sortedOptions && sortedItems && [...sortedOptions, ...filterOutBrandItems];
  }, [options, itemsWithIngredientId]);

  if (loading || !options || !menuItems) {
    return (
      <Box sx={{ my: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <>
      <FieldArray name="modifierItems">
        {({ push, remove, form }) => (
          <>
            <Typography className={classes.title} variant="h4">
              Items and ingredients
            </Typography>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable
                droppableId="droppable"
                type="droppable_options"
                isDropDisabled={!allowReordering}
              >
                <>
                  {itemOptions.map((itemOption, index) => (
                    <Draggable
                      key={itemOption.itemName.length > 0 ? itemOption.itemName : nanoid()}
                      draggableId={`option${itemOption.itemName}`}
                      index={index}
                      isDragDisabled={!allowReordering}
                      className={classes.optionField}
                      dragIconClassName={classes.dragIcon}
                    >
                      <Grid container key={itemOption.itemName} spacing={1}>
                        <Grid item xs={4} sm={6}>
                          <Field
                            className={classes.ingredientAutocomplete}
                            component={Autocomplete}
                            fullWidth
                            disableClearable
                            options={searchableOptions}
                            getOptionLabel={(option) => option.ingredientName || option.itemName}
                            name={`modifierItems.${index}.itemName`}
                            value={searchableOptions.find(
                              (option) =>
                                option.ingredientId === itemOption.itemId ||
                                option.itemId === itemOption.itemId,
                            )}
                            groupBy={(option) => (option.isItem ? 'Items' : 'Ingredients')}
                            variant="outlined"
                            margin="normal"
                            disabled={readonly || searchableOptions?.length === 0}
                            onChange={(_, value) => {
                              if (
                                !itemOptions.find(
                                  (item) =>
                                    item.itemId === value.ingredientId ||
                                    item.itemId === value.itemId,
                                )
                              ) {
                                setShowDuplicateHelperText(false);
                                form.setFieldValue(
                                  `modifierItems.${index}.itemName`,
                                  value.ingredientName || value.itemName,
                                );
                                form.setFieldValue(
                                  `modifierItems.${index}.itemId`,
                                  value.ingredientId || value.itemId,
                                );
                              } else {
                                form.setFieldValue(`modifierItems.${index}.itemName`, '');
                                form.setFieldValue(`modifierItems.${index}.itemId`, '');
                                setShowDuplicateHelperText(true);
                              }
                            }}
                            renderInput={(params) => (
                              <MuiTextField
                                {...params}
                                label={getLabel(itemOption.itemId)}
                                error={showDuplicateHelperText && itemOption.itemId === ''}
                                helperText={
                                  showDuplicateHelperText && itemOption.itemId === ''
                                    ? 'Duplicate selection found, select another item or ingredient'
                                    : null
                                }
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Field
                            type="number"
                            component={TextField}
                            fullWidth
                            inputProps={{ step: '1', min: 1 }}
                            name={`modifierItems.${index}.multiMax`}
                            label="Multiples"
                            variant="outlined"
                            margin="normal"
                            disabled={readonly}
                          />
                        </Grid>
                        <Grid item xs={4} sm={2}>
                          <Field
                            type="number"
                            component={TextField}
                            fullWidth
                            inputProps={{ step: '0.01', min: 0 }}
                            name={`modifierItems.${index}.itemPrice`}
                            label="Price (£)"
                            variant="outlined"
                            margin="normal"
                            disabled={readonly}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={2}
                          style={{
                            paddingTop: '25px',
                            textAlign: 'center',
                          }}
                        >
                          {itemOptions.length > 1 && (
                            <IconButton
                              disabled={readonly}
                              aria-label="delete"
                              onClick={() => {
                                remove(index);
                              }}
                              size="large"
                            >
                              <Delete />
                            </IconButton>
                          )}
                        </Grid>
                      </Grid>
                    </Draggable>
                  ))}
                </>
              </Droppable>
            </DragDropContext>

            <Button
              color="primary"
              disabled={readonly}
              startIcon={<AddCircleOutline style={{ fontSize: 30 }} />}
              sx={{ mt: 2 }}
              onClick={() => {
                push({ itemName: '', itemPrice: 0, itemId: '', multiMax: 1 });
              }}
            >
              Add another ingredient
            </Button>
          </>
        )}
      </FieldArray>
      <CustomDialog
        title="Add a new item"
        handleCloseDialog={handleCloseSizeDialog}
        isDialogOpen={isSizeDialogOpen}
      >
        <AddIngredient modalSubmit={handleCloseSizeDialog} modalClose={handleCloseSizeDialog} />
      </CustomDialog>
    </>
  );
};

ModifierOptions.defaultProps = {
  allowReordering: true,
  onReorder: null,
};

ModifierOptions.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  itemOptions: PropTypes.array.isRequired,
  allowReordering: PropTypes.bool,
  onReorder: PropTypes.func,
};

export default ModifierOptions;
